/**
 * Sous-total de chaque palier d'une gamme : ce que coûtent, au prix public, les pièces à remplacer
 * à une visite (demande de Benjamin, 2026-10-14 : « visite 3 000 h = x € »).
 *
 * Règles, toutes pour qu'un total n'ait JAMAIS l'air complet quand il ne l'est pas :
 * - seules les lignes de REMPLACEMENT comptent (quantité × prix public) ;
 * - une ligne « si nécessaire » n'est pas comptée (on ne sait pas si elle servira) : le palier le dit ;
 * - une ligne de remplacement sans prix, ou sans quantité, n'est pas comptée non plus : le palier est
 *   alors dit INCOMPLET, jamais présenté avec un montant qui paraîtrait juste ;
 * - le prix s'arrondit au centime ligne par ligne (comme une facture), la somme se fait en centimes
 *   entiers : pas de dérive de virgule flottante ;
 * - sans le droit de voir les prix (les clés de prix sont ABSENTES de la réponse), il n'y a pas de
 *   sous-total du tout : un total ne doit pas révéler ce que les colonnes cachent.
 *
 * LOT P2 (registre B26) : un sous-total se lit selon la SOURCE du prix — le prix du TARIF du fabricant (défaut, le
 * comportement d'origine), le prix de VENTE (ce que voit le client) ou le coût d'ACHAT réel. Mêmes règles, mêmes
 * arrondis, quelle que soit la source. La MARGE d'un palier ne compare que les lignes qui ont à la fois un prix de
 * vente et un coût d'achat : jamais une marge calculée sur des montants qui ne se correspondent pas.
 */
import type { GammeLigne, GammeNiveau } from '../api/client';
import { accorde } from './format';
import { ligneAvecPrix, prix } from './gammes';
import { prixDeLaLigne, type GammeLigneVue, type GammeNiveauVue, type SourcePrix } from './gammes-prix-calcules';

export interface SousTotalPalier {
  readonly code: string;
  /** Somme des lignes de remplacement chiffrées, en centimes entiers. */
  readonly centimes: number;
  /** Lignes de remplacement comptées dans la somme. */
  readonly comptees: number;
  /** Lignes de remplacement non comptées : prix ou quantité manquant. */
  readonly nonChiffrees: number;
  /** Lignes « si nécessaire » (non comptées par choix). */
  readonly siNecessaire: number;
  /** Faux dès qu'une ligne de remplacement n'a pas pu être comptée. */
  readonly complet: boolean;
}

/**
 * Total d'une ligne, en centimes entiers, ou `null` quand elle ne compte pas (autre genre que
 * « remplacement », prix ou quantité absent).
 */
export function totalLigneCentimes(ligne: GammeLigneVue, source: SourcePrix = 'TARIF'): number | null {
  if (ligne.kind !== 'REPLACE') return null;
  if (ligne.quantity === null) return null;
  const unitaire = prixDeLaLigne(ligne, source);
  if (unitaire === null) return null;
  return Math.round(Math.round(unitaire * 100) * ligne.quantity);
}

/** `null` quand le palier n'a aucune ligne ou que les prix ne sont pas visibles pour cet appelant. */
export function sousTotalPalier(niveau: GammeNiveauVue | GammeNiveau, source: SourcePrix = 'TARIF'): SousTotalPalier | null {
  if (niveau.lines.length === 0) return null;
  if (!niveau.lines.some(ligneAvecPrix)) return null;
  // Un sous-total au prix de vente ou au coût d'achat n'existe que si au moins une ligne en porte un.
  if (source !== 'TARIF' && !niveau.lines.some((l) => prixDeLaLigne(l, source) !== null)) return null;

  let centimes = 0;
  let comptees = 0;
  let nonChiffrees = 0;
  let siNecessaire = 0;
  for (const ligne of niveau.lines) {
    if (ligne.kind === 'CONDITIONAL') {
      siNecessaire += 1;
      continue;
    }
    if (ligne.kind !== 'REPLACE') continue;
    const total = totalLigneCentimes(ligne, source);
    if (total === null) {
      nonChiffrees += 1;
    } else {
      centimes += total;
      comptees += 1;
    }
  }
  return { code: niveau.code, centimes, comptees, nonChiffrees, siNecessaire, complet: nonChiffrees === 0 };
}

/**
 * Un sous-total par palier, dans l'ordre de la gamme ; `null` quand aucun prix n'est visible ou
 * qu'aucun palier n'a de ligne.
 */
export function sousTotauxDeLaVersion(
  niveaux: readonly (GammeNiveauVue | GammeNiveau)[],
  source: SourcePrix = 'TARIF',
): SousTotalPalier[] | null {
  const liste = niveaux.map((n) => sousTotalPalier(n, source)).filter((s): s is SousTotalPalier => s !== null);
  return liste.length > 0 ? liste : null;
}

/* ------------------------------------------------------------------- marge */

/**
 * La marge d'un palier : prix de vente − coût d'achat réel, sur les SEULES lignes qui ont les deux (et une quantité).
 * Une ligne qui n'a que l'un des deux (condition manquante côté marque ou côté équivalent) n'est pas comparée : la
 * marge ne mélange jamais des montants qui ne se correspondent pas. `exclues` dit combien de lignes de remplacement
 * sont restées à l'écart, `complete` n'est vrai que si aucune ne l'est.
 */
export interface MargePalier {
  readonly code: string;
  /** Somme des prix de vente des lignes comparées, en centimes entiers. */
  readonly venteCentimes: number;
  /** Somme des coûts d'achat de ces mêmes lignes, en centimes entiers. */
  readonly achatCentimes: number;
  readonly margeCentimes: number;
  /** Marge ÷ prix de vente, en pourcentage ; `null` quand la vente comparée est nulle. */
  readonly tauxMarge: number | null;
  readonly comparees: number;
  readonly exclues: number;
  readonly complete: boolean;
}

/** `null` quand aucune ligne n'a à la fois un prix de vente et un coût d'achat. */
export function margePalier(niveau: GammeNiveauVue): MargePalier | null {
  let venteCentimes = 0;
  let achatCentimes = 0;
  let comparees = 0;
  let exclues = 0;
  for (const ligne of niveau.lines) {
    if (ligne.kind !== 'REPLACE') continue;
    const vente = totalLigneCentimes(ligne, 'VENTE');
    const achat = totalLigneCentimes(ligne, 'ACHAT');
    if (vente !== null && achat !== null) {
      venteCentimes += vente;
      achatCentimes += achat;
      comparees += 1;
    } else {
      exclues += 1;
    }
  }
  if (comparees === 0) return null;
  const margeCentimes = venteCentimes - achatCentimes;
  return {
    code: niveau.code,
    venteCentimes,
    achatCentimes,
    margeCentimes,
    tauxMarge: venteCentimes === 0 ? null : (margeCentimes / venteCentimes) * 100,
    comparees,
    exclues,
    complete: exclues === 0,
  };
}

/** « A : 89,00 € (40,0 %) · B : 120,00 € (35,0 %) (partielle) » : la marge de chaque palier, d'un coup d'œil. */
export function recapitulatifMarges(marges: readonly MargePalier[]): string {
  return marges.map((m) => `${m.code} : ${montantMarge(m)}${m.complete ? '' : ' (partielle)'}`).join(' · ');
}

/** « 40,0 % » ; « — » quand la vente comparée est nulle. */
export function formaterTaux(taux: number | null): string {
  return taux === null ? '—' : `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(taux)} %`;
}

/** « 1 234,00 € (40,0 %) » : la marge d'un palier, montant puis taux de marque. */
export function montantMarge(m: MargePalier): string {
  return `${prix(m.margeCentimes / 100)} (${formaterTaux(m.tauxMarge)})`;
}

/** Ce qui reste à dire sous une marge pour qu'on ne la prenne pas pour plus qu'elle n'est, ou `null`. */
export function reserveMarge(m: MargePalier): string | null {
  if (m.exclues === 0) return null;
  const n = m.exclues;
  return `${n} ${accorde(n, 'ligne')} sans prix de vente ou sans coût d’achat ${accorde(n, 'n’est pas comparée', 'ne sont pas comparées')} : la marge est partielle.`;
}

/** « 143,00 € » ; « — » quand rien n'est chiffré (jamais « 0,00 € » inventé). */
export function montantSousTotal(s: SousTotalPalier): string {
  return s.comptees === 0 ? '—' : prix(s.centimes / 100);
}

/**
 * Ce qui reste à dire sous un sous-total pour qu'on ne le prenne pas pour plus qu'il n'est, ou
 * `null` quand il n'y a rien à ajouter.
 */
export function reserveSousTotal(s: SousTotalPalier): string | null {
  const morceaux: string[] = [];
  if (s.nonChiffrees > 0) {
    const n = s.nonChiffrees;
    morceaux.push(
      `${n} ${accorde(n, 'ligne')} de remplacement sans prix ou sans quantité ${accorde(
        n,
        'n’est pas comptée',
        'ne sont pas comptées',
      )}, le total est incomplet`,
    );
  }
  if (s.siNecessaire > 0) {
    const n = s.siNecessaire;
    morceaux.push(
      `${n} ${accorde(n, 'ligne')} « si nécessaire » ${accorde(n, 'n’est pas comptée', 'ne sont pas comptées')}`,
    );
  }
  return morceaux.length > 0 ? `${morceaux.join(' ; ')}.` : null;
}

/* ------------------------------------------------------------- équivalents */

/**
 * Le côté « interne » d'un palier : ce que coûte la même visite avec les ÉQUIVALENTS (l'« adaptable »)
 * que Benjamin achète à la place de la pièce d'origine (demande du 2026-10-14 : « les sous-totaux de
 * l'adaptable »). Il n'apparaît que dans la variante interne : un client ne voit jamais ces prix.
 *
 * Deux chiffres, nommés pour ne pas être confondus :
 * - le SOUS-TOTAL ADAPTABLE : quantité × prix de l'équivalent, des seules lignes qui en ont un ;
 * - le COÛT D'ACHAT ESTIMÉ : le prix de l'équivalent quand il existe, sinon le prix public (la pièce
 *   d'origine, achetée telle quelle). C'est une estimation : le prix public n'est pas un prix d'achat.
 *
 * Même arrondi que le public : au centime, ligne par ligne, somme en centimes entiers.
 */
export interface SousTotalAdaptable {
  readonly code: string;
  /** Somme des lignes qui ont un équivalent chiffré, en centimes entiers. */
  readonly adaptableCentimes: number;
  /** Lignes de remplacement comptées dans cette somme. */
  readonly avecEquivalent: number;
  /** Lignes de remplacement sans prix d'équivalent : achetées au prix public. */
  readonly sansEquivalent: number;
  /** Coût d'achat estimé : équivalent s'il existe, sinon prix public, en centimes entiers. */
  readonly achatCentimes: number;
  /** Faux quand une ligne n'a ni prix d'équivalent ni prix public (ou pas de quantité) : le coût est incomplet. */
  readonly achatComplet: boolean;
}

/** Total d'une ligne au prix de l'ÉQUIVALENT, en centimes entiers, ou `null` s'il n'en a pas. */
export function totalAdaptableLigneCentimes(ligne: GammeLigne): number | null {
  if (ligne.kind !== 'REPLACE') return null;
  if (ligne.quantity === null) return null;
  if (ligne.internalPrice === null || ligne.internalPrice === undefined) return null;
  return Math.round(Math.round(ligne.internalPrice * 100) * ligne.quantity);
}

/**
 * `null` quand le palier n'a aucune ligne, que les prix ne sont pas visibles, ou qu'aucune ligne de
 * remplacement n'a d'équivalent : il n'y a alors rien à comparer.
 */
export function sousTotalAdaptable(niveau: GammeNiveau): SousTotalAdaptable | null {
  if (niveau.lines.length === 0) return null;
  if (!niveau.lines.some(ligneAvecPrix)) return null;

  let adaptableCentimes = 0;
  let avecEquivalent = 0;
  let sansEquivalent = 0;
  let achatCentimes = 0;
  let achatComplet = true;
  for (const ligne of niveau.lines) {
    if (ligne.kind !== 'REPLACE') continue;
    const adaptable = totalAdaptableLigneCentimes(ligne);
    if (adaptable !== null) {
      adaptableCentimes += adaptable;
      avecEquivalent += 1;
      achatCentimes += adaptable;
      continue;
    }
    const public_ = totalLigneCentimes(ligne);
    if (public_ === null) {
      achatComplet = false;
    } else {
      sansEquivalent += 1;
      achatCentimes += public_;
    }
  }
  if (avecEquivalent === 0) return null;
  return { code: niveau.code, adaptableCentimes, avecEquivalent, sansEquivalent, achatCentimes, achatComplet };
}

/** Ce que veut dire « coût d'achat estimé » : à écrire partout où il apparaît. */
export const EXPLICATION_COUT_ACHAT = 'Coût d’achat estimé : prix de l’équivalent quand il existe, sinon prix public.';

/** Phrase sous le coût d'achat : ce qui est estimé, et ce qui manque. */
export function reserveAdaptable(s: SousTotalAdaptable): string {
  const base = EXPLICATION_COUT_ACHAT;
  const morceaux: string[] = [];
  if (s.sansEquivalent > 0) {
    const n = s.sansEquivalent;
    morceaux.push(
      `${n} ${accorde(n, 'ligne')} sans équivalent ${accorde(n, 'est comptée', 'sont comptées')} au prix public`,
    );
  }
  if (!s.achatComplet) morceaux.push('au moins une ligne n’a aucun prix : le coût est incomplet');
  if (morceaux.length === 0) return base;
  const suite = morceaux.join(' ; ');
  return `${base} ${suite.charAt(0).toUpperCase()}${suite.slice(1)}.`;
}

/** « A : 560,00 € · C : 1 210,00 € (incomplet) » : le coût d'achat estimé des paliers qui ont des équivalents. */
export function recapitulatifAchat(adaptables: readonly SousTotalAdaptable[]): string {
  return adaptables.map((s) => `${s.code} : ${prix(s.achatCentimes / 100)}${s.achatComplet ? '' : ' (incomplet)'}`).join(' · ');
}

/** « A : 143,00 € · B : 1 366,00 € (incomplet) · … » : la lecture d'un coup d'œil. */
export function recapitulatifSousTotaux(sousTotaux: readonly SousTotalPalier[]): string {
  return sousTotaux
    .map((s) => `${s.code} : ${montantSousTotal(s)}${s.complet ? '' : ' (incomplet)'}`)
    .join(' · ');
}
